import { useCallback, useEffect, useState } from 'react';
import type { PendingAction, Slot, ToolOutput } from '@ampshift/shared';
import type { ApiClient } from '../api';
import { useI18n, formatDateTime } from '../i18n';
import { ConfirmDialog } from './ConfirmDialog';
import { SlotPickerView } from './Surfaces';

type AppointmentView = ToolOutput<'getAppointments'>['appointments'][number];

interface Props { api: ApiClient; refreshKey: number; onChanged: () => void; onChat: (text: string) => Promise<void> }

type Draft =
  | { kind: 'cancel'; appointment: AppointmentView; pending: PendingAction }
  | { kind: 'reschedule'; appointment: AppointmentView; pending: PendingAction; slot: Slot };

const localIsoDate = (d: Date): string => d.toISOString().slice(0, 10);
const plusDays = (d: Date, n: number): Date => new Date(d.getTime() + n * 86_400_000);

/** "My appointments": direct tool calls; every change goes draft → confirm dialog → /v1/chat/confirm. */
export function AppointmentsCard({ api, refreshKey, onChanged, onChat }: Props) {
  const { t, lang } = useI18n();
  const [appointments, setAppointments] = useState<AppointmentView[] | null>(null);
  const [timeZone, setTimeZone] = useState('Asia/Riyadh');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [rescheduling, setRescheduling] = useState<{ appointment: AppointmentView; slots: Slot[] } | null>(null);
  const [attendance, setAttendance] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const today = new Date();
      const out = await api.tool('getAppointments', { from: localIsoDate(today), to: localIsoDate(plusDays(today, 120)), status: ['BOOKED', 'CHECKED_IN', 'DRAFT'] });
      setAppointments(out.appointments.filter((a) => a.status === 'BOOKED' || a.status === 'CHECKED_IN').sort((a, b) => a.start.localeCompare(b.start)));
      setTimeZone(out.timeZone);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
      setAppointments([]);
    }
  }, [api, t]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const startCancel = async (a: AppointmentView) => {
    setBusyId(a.appointmentId);
    setNotice(null);
    try {
      const out = await api.tool('cancelAppointmentDraft', { appointmentId: a.appointmentId });
      setDraft({ kind: 'cancel', appointment: a, pending: out.pendingAction });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    } finally {
      setBusyId(null);
    }
  };

  const startReschedule = async (a: AppointmentView) => {
    setBusyId(a.appointmentId);
    setNotice(null);
    try {
      const from = localIsoDate(new Date());
      const out = await api.tool('findAvailableSlots', { providerId: a.providerId, appointmentTypeCode: a.appointmentTypeCode, from, to: localIsoDate(plusDays(new Date(), 14)), limit: 12 });
      setRescheduling({ appointment: a, slots: out.slots.filter((s) => s.start !== a.start) });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    } finally {
      setBusyId(null);
    }
  };

  const pickRescheduleSlot = async (slot: Slot) => {
    if (!rescheduling) return;
    const a = rescheduling.appointment;
    try {
      const out = await api.tool('rescheduleAppointmentDraft', { appointmentId: a.appointmentId, providerId: slot.providerId, start: slot.start });
      setDraft({ kind: 'reschedule', appointment: a, pending: out.pendingAction, slot });
      setRescheduling(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
    }
  };

  const confirmDraft = async () => {
    if (!draft) return;
    await api.confirm({ pendingActionId: draft.pending.pendingActionId, decision: 'confirm', acknowledgedFindingIds: [] });
    setNotice(draft.kind === 'cancel' ? t('cancelled') : t('rescheduled'));
    setDraft(null);
    onChanged();
  };

  const rejectDraft = () => {
    const d = draft;
    setDraft(null);
    if (d) void api.confirm({ pendingActionId: d.pending.pendingActionId, decision: 'reject', acknowledgedFindingIds: [] }).catch(() => undefined);
  };

  const confirmAttendance = async (a: AppointmentView, checked: boolean) => {
    setAttendance((prev) => ({ ...prev, [a.appointmentId]: checked }));
    if (checked) await onChat(`confirm attendance for appointment ${a.appointmentId}`);
  };

  const statusPill = (s: AppointmentView['status']) => <span className={`pill ${s === 'CHECKED_IN' ? 'pill-ok' : ''}`}>{s.replace('_', ' ')}</span>;

  return (
    <section className="card" aria-labelledby="appts-title">
      <h2 id="appts-title">{t('myAppointments')}</h2>
      {error && <div className="err" role="alert">{error} <button className="btn-link" onClick={() => void load()}>{t('retry')}</button></div>}
      {notice && <div className="ok" role="status">{notice}</div>}
      {appointments === null ? <div className="muted">{t('loading')}</div> : appointments.length === 0 ? <div className="muted">{t('noAppointments')}</div> : (
        <div className="stack">
          {appointments.map((a) => (
            <article key={a.appointmentId} className="appt" aria-label={`${a.providerName ?? a.providerId} ${formatDateTime(a.start, lang, timeZone)}`}>
              <div className="appt-head">
                <span className="appt-when">{formatDateTime(a.start, lang, timeZone)}</span>
                {statusPill(a.status)}
              </div>
              <div>{a.providerName ?? a.providerId} <span className="muted">· {a.appointmentTypeCode}{a.reason ? ` · ${a.reason}` : ''}</span></div>
              <div className="row">
                <button className="btn btn-sm" onClick={() => void startReschedule(a)} disabled={busyId === a.appointmentId}>{t('reschedule')}</button>
                <button className="btn btn-sm btn-danger" onClick={() => void startCancel(a)} disabled={busyId === a.appointmentId}>{t('cancel')}</button>
                <label className="row" style={{ marginInlineStart: 'auto', fontSize: '.85rem' }}>
                  <input type="checkbox" checked={attendance[a.appointmentId] ?? false} onChange={(e) => void confirmAttendance(a, e.target.checked)} />
                  {attendance[a.appointmentId] ? t('attendanceConfirmed') : t('confirmAttendance')}
                </label>
              </div>
              {rescheduling?.appointment.appointmentId === a.appointmentId && (
                rescheduling.slots.length === 0 ? <div className="muted">{t('noSlots')}</div>
                  : <SlotPickerView s={{ type: 'slot-picker', title: t('pickSlot'), slots: rescheduling.slots }} onPick={(s) => void pickRescheduleSlot(s)} />
              )}
            </article>
          ))}
        </div>
      )}
      {draft && (
        <ConfirmDialog
          title={draft.kind === 'cancel' ? t('cancelAppointmentTitle') : t('rescheduleTitle')}
          lines={[
            { label: t('provider'), value: draft.appointment.providerName ?? draft.appointment.providerId },
            { label: t('date'), value: formatDateTime(draft.appointment.start, lang, timeZone) },
            ...(draft.kind === 'reschedule' ? [{ label: '→', value: formatDateTime(draft.slot.start, lang, timeZone) }] : []),
            { label: t('status'), value: draft.pending.summary },
          ]}
          onConfirm={confirmDraft}
          onCancel={rejectDraft}
        />
      )}
    </section>
  );
}
